import { type FC } from 'react';

import { RefreshCw } from 'lucide-react';
import { cn } from 'tailwind-variants';

import { GenericButton, GenericTooltip } from '@shared/components/ui/custom';
import { Skeleton } from '@shared/components/ui/skeleton';
import { formatNumber, truncateText } from '@shared/utils/formatters.utils';

const DEFAULT_ERROR_MESSAGE = 'Failed to load tokens balance.';
const INLINE_MESSAGE_MAX_LENGTH = 60;
const TOOLTIP_MESSAGE_MAX_LENGTH = 300;

interface TokenAvailabilityProps {
  lastUpdated: string;
  availableTokens: number | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  handleRefresh: () => void;
}

export const TokenAvailability: FC<TokenAvailabilityProps> = ({
  lastUpdated,
  availableTokens,
  isLoading,
  isError,
  errorMessage,
  handleRefresh,
}) => {
  const isResolved = availableTokens !== null;
  const showError = isError && !isLoading;

  if (showError) {
    const message = errorMessage ?? DEFAULT_ERROR_MESSAGE;
    const isTooLongToFit = message.length > INLINE_MESSAGE_MAX_LENGTH;

    const errorLabel = (
      <span className={cn('text-error-foreground text-xs', isTooLongToFit && 'cursor-help')}>
        {isTooLongToFit ? truncateText(message, INLINE_MESSAGE_MAX_LENGTH) : message}
      </span>
    );

    return (
      <div
        className="flex items-center gap-2 text-sm h-6"
        data-testid="add-new-investigation-failed-to-retrieve-tokens-msg"
      >
        {isTooLongToFit ? (
          <GenericTooltip
            content={truncateText(message, TOOLTIP_MESSAGE_MAX_LENGTH)}
            side="top"
            align="start"
            className="max-w-xs wrap-break-word"
          >
            {errorLabel}
          </GenericTooltip>
        ) : (
          errorLabel
        )}
        <GenericButton
          type="button"
          variant="ghost"
          size="sm"
          icon={RefreshCw}
          label="Retry"
          onClick={handleRefresh}
          className="text-muted-foreground hover:text-foreground h-6 px-2"
          data-testid="add-new-investigation-retry-refresh-tokens-button"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm h-6">
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Available Tokens:</span>
        {isResolved ? (
          <span
            className="font-semibold text-foreground text-base leading-none"
            data-testid="add-new-investigation-available-tokens"
          >
            {formatNumber(availableTokens ?? 0)}
          </span>
        ) : (
          <Skeleton className="h-4 w-8 rounded-sm" />
        )}
      </div>

      <span className="text-muted-foreground/30">|</span>

      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
        {isResolved ? (
          <>
            <span>as of {lastUpdated}</span>
            <GenericButton
              type="button"
              variant="ghost"
              icon={RefreshCw}
              onClick={handleRefresh}
              disabled={isLoading}
              className={cn('h-6 w-6 p-1', isLoading && '[&_svg]:animate-spin')}
              title="Refresh tokens balance"
              data-testid="add-new-investigation-refresh-tokens-button"
            />
          </>
        ) : (
          <Skeleton className="h-4 w-32 rounded-sm" />
        )}
      </div>
    </div>
  );
};
